import {
  TREATMENT_API_AUTH_SCHEME,
  TREATMENT_API_DEFAULTS,
  TREATMENT_API_PATH,
  TREATMENT_CHECKOUT_OPERATION,
  TREATMENT_RUNTIME_ACTION,
  TREATMENT_STORAGE_KEY,
  TREATMENT_SOURCE_KIND,
} from "./treatmentApiConstants";
import { TREATMENT_CHECKOUT_ERROR_COPY } from "./treatmentUiConstants";
import { normalizeProductSelections } from "./checkoutContract";
import {
  treatmentRuntimeApiUrl,
} from "./runtimeApiBase";
import { normalizeRuntimeAuthority } from "./runtimeAuthority";
import { withLaunchReason } from "./launchReason";
import {
  refreshPatientAccessToken,
  storedPatientAccessTokenNeedsRefresh,
} from "./authentication/patientAccessRefresh";

const SAFE_RUNTIME_ERROR_COPY = Object.freeze({
  runtime_release_integrity_invalid:
    "This treatment is temporarily unavailable. Please try again later.",
  question_identity_ambiguous:
    "This treatment release has an ambiguous question. Refresh or start over.",
  question_identity_duplicate:
    "This treatment release has a duplicate question. Refresh or start over.",
  hidden_answer_rejected:
    "That answer is no longer part of this treatment step. Refresh and try again.",
  runtime_pointer_invalid:
    "Saved treatment progress is invalid. Start over to continue.",
  runtime_navigation_target_unavailable:
    "That treatment step is no longer available. Refresh and try again.",
  checkpoint_invalid: "Please review this answer and try again.",
});

const containsRawIdentity = (value) => (
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/i
    .test(String(value || ""))
);

const runtimeSessionResponse = (session, extras = {}) => (
  normalizeRuntimeAuthority({ ...session, ...extras })
);

const validationMessage = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(validationMessage).filter(Boolean).join(" ");
  if (typeof value === "object") {
    return Object.values(value)
      .map(validationMessage)
      .filter(Boolean)
      .join(" ");
  }
  return String(value);
};

export const normalizeTreatmentRuntimeErrorCode = (code) => (
  code === "stale_revision" ? "runtime_revision_stale" : code
);

export const isTerminalTreatmentSessionError = (error) => {
  const status = Number(error?.status || error?.response?.status || 0);
  const code = String(
    error?.code || error?.rawCode || error?.response?.data?.code || "",
  ).toLowerCase();
  return (
    status === 404
    || status === 410
    || [
      "runtime_session_expired",
      "runtime_session_not_found",
      "runtime_session_invalid",
      "session_expired",
      "session_not_found",
      "session_invalid",
    ].includes(code)
  );
};

const parse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (response.ok) return data;
  const rawCode = data.code || data.error;
  const code = normalizeTreatmentRuntimeErrorCode(rawCode);
  const serverMessage = (
    validationMessage(data.message)
    || validationMessage(data.detail)
    || validationMessage(data.details)
  );
  const errorValue = validationMessage(data.error);
  const errorIsCode = (
    errorValue === code
    && /^[a-z0-9_]+$/.test(errorValue)
  );
  const safeServerMessage = containsRawIdentity(serverMessage)
    ? ""
    : serverMessage;
  const error = new Error(
    TREATMENT_CHECKOUT_ERROR_COPY[code]
      || SAFE_RUNTIME_ERROR_COPY[code]
      || safeServerMessage
      || (errorIsCode ? "" : (containsRawIdentity(errorValue) ? "" : errorValue))
      || "Treatment session request failed.",
  );
  error.code = code;
  error.rawCode = rawCode;
  error.status = response.status;
  if (response.status === 429) {
    const retryAfter = response.headers?.get?.("Retry-After");
    const retryAfterSeconds = Number(retryAfter);
    if (retryAfter !== null && retryAfter !== undefined
      && Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
      error.retryAfterSeconds = Math.ceil(retryAfterSeconds);
    }
    error.message = error.retryAfterSeconds
      ? `Too many requests. Please try again in about ${Math.ceil(error.retryAfterSeconds / 60)} ${error.retryAfterSeconds <= 60 ? "minute" : "minutes"}.`
      : "Too many requests. Please wait a moment, then try again.";
  }
  error.current = data.current;
  error.details = data.details;
  error.checkoutRecovery = data.checkout_recovery;
  error.traceId = data.trace_id;
  error.retryable = data.retryable;
  throw error;
};

const accessTokenRejected = (status, data) => {
  // This tenant returns expired SimpleJWT access tokens as 403. Other DRF
  // setups use 401 for the same body. Either status must refresh before the
  // questionnaire renders data.detail.
  if (status !== 401 && status !== 403) return false;
  const messages = Array.isArray(data?.messages) ? data.messages : [];
  return (
    data?.code === "token_not_valid"
    || data?.detail === "Given token not valid for any token type"
    || messages.some((message) => (
      message?.code === "token_not_valid"
      || message?.message === "Token is expired"
    ))
  );
};

const request = (
  path,
  options = {},
) => {
  const {
    method = "GET",
    body,
    patientAccessToken,
    previewCapabilityToken,
    apiBaseUrl,
    formData,
    includePatientAccessToken = true,
    timeoutMs = TREATMENT_API_DEFAULTS.requestTimeoutMs,
    sessionRefreshAttempted = false,
  } = options;
  const requestUrl = treatmentRuntimeApiUrl(apiBaseUrl, path);
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(
    () => controller.abort(),
    timeoutMs,
  );
  const canRefreshPatientSession = (
    includePatientAccessToken
    && !previewCapabilityToken
    && !sessionRefreshAttempted
  );

  const send = (token) => {
    const headers = formData ? {} : { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (previewCapabilityToken) {
      headers.Authorization = (
        `${TREATMENT_API_AUTH_SCHEME.previewCapability} `
        + previewCapabilityToken
      );
    }
    return fetch(requestUrl, {
      method,
      headers,
      body: formData || (body === undefined ? undefined : JSON.stringify(body)),
      signal: controller.signal,
    });
  };

  const resolveToken = async () => {
    if (!includePatientAccessToken) return null;
    const stored = (
      patientAccessToken
      || sessionStorage.getItem(TREATMENT_STORAGE_KEY.patientAccessToken)
    );
    if (
      canRefreshPatientSession
      && stored
      && storedPatientAccessTokenNeedsRefresh()
    ) {
      return refreshPatientAccessToken(apiBaseUrl);
    }
    return stored;
  };

  return resolveToken()
    .then((token) => send(token))
    .then(async (response) => {
      if (
        !canRefreshPatientSession
        || response.ok
        || typeof response.clone !== "function"
      ) return parse(response);
      const data = await response.clone().json().catch(() => ({}));
      if (!accessTokenRejected(response.status, data)) return parse(response);
      const fresh = await refreshPatientAccessToken(apiBaseUrl);
      return request(path, {
        ...options,
        patientAccessToken: fresh,
        sessionRefreshAttempted: true,
      });
    })
    .catch((error) => {
      if (error?.name === "AbortError") {
        const timeoutError = new Error(
          "Treatment session request timed out. Please try again.",
        );
        timeoutError.code = "runtime_request_timeout";
        timeoutError.retryable = true;
        throw timeoutError;
      }
      throw error;
    })
    .finally(() => globalThis.clearTimeout(timeout));
};

export const createTreatmentSession = (
  sourceKind,
  sourceId,
  release,
  apiBaseUrl,
) => request(
  withLaunchReason(
    sourceKind === TREATMENT_SOURCE_KIND.program
      ? TREATMENT_API_PATH.programRuntimeSessions(sourceId)
      : TREATMENT_API_PATH.customProgramRuntimeSessions(sourceId),
  ),
  {
    method: "POST",
    apiBaseUrl,
    // Session creation is intentionally anonymous. A stale patient JWT in
    // storage must not make DRF reject this public endpoint before the runtime
    // session can be created and the patient can authenticate later.
    includePatientAccessToken: false,
    body: {
      mode: "live",
      release_token: release?.public_token,
      release_version: release?.version,
    },
  },
).then((session) => runtimeSessionResponse(session, {
  runtime_api_base_url: apiBaseUrl,
}));

export const createTreatmentPreviewSession = (
  previewCapabilityToken,
  apiBaseUrl = null,
  previewPatient = null,
) => request(
  TREATMENT_API_PATH.previewRuntimeSessions,
  {
    method: "POST",
    previewCapabilityToken,
    apiBaseUrl,
    ...(previewPatient ? { body: { preview_patient: previewPatient } } : {}),
  },
).then((session) => runtimeSessionResponse(session, {
  preview_capability_token: previewCapabilityToken,
  preview_api_base_url: apiBaseUrl,
}));

export const getTreatmentSession = (
  sessionToken,
  previewCapabilityToken = null,
  apiBaseUrl,
) => request(
  previewCapabilityToken
    ? TREATMENT_API_PATH.previewRuntimeSessionDetail(sessionToken)
    : TREATMENT_API_PATH.runtimeSessionDetail(sessionToken),
  { previewCapabilityToken, apiBaseUrl },
).then((session) => runtimeSessionResponse(session, {
  ...(previewCapabilityToken
    ? {
        preview_capability_token: previewCapabilityToken,
        preview_api_base_url: apiBaseUrl,
      }
    : { runtime_api_base_url: apiBaseUrl }),
}));

export const bindTreatmentPatient = (session, patientAccessToken, profile) => request(
  TREATMENT_API_PATH.runtimeSessionBindPatient(session.session_token),
  {
    method: "POST",
    patientAccessToken,
    apiBaseUrl: session.runtime_api_base_url,
    body: { expected_revision: session.session_revision, profile },
  },
).then((next) => runtimeSessionResponse(next, {
  runtime_api_base_url: session.runtime_api_base_url,
}));

export const transitionTreatmentSession = (session, action, payload) => {
  const checkoutCapability = (
    action === TREATMENT_RUNTIME_ACTION.selectProducts
    && !session.preview_capability_token
    && session.checkout_capability
  );
  return request(
    session.preview_capability_token
      ? TREATMENT_API_PATH.previewRuntimeSessionTransition(session.session_token)
      : TREATMENT_API_PATH.runtimeSessionTransition(session.session_token),
    {
      method: "POST",
      // A recovered checkout can be edited in a fresh browser. Its capability
      // authorizes this quote change; an unrelated saved JWT must not block it.
      includePatientAccessToken: !checkoutCapability,
      previewCapabilityToken: session.preview_capability_token,
      apiBaseUrl: session.preview_api_base_url || session.runtime_api_base_url,
      body: {
        expected_revision: session.session_revision,
        action,
        ...(checkoutCapability ? { checkout_capability: checkoutCapability } : {}),
        ...(session.cursor_node_id !== undefined
          ? { cursor_node_id: session.cursor_node_id || "" }
          : {}),
        payload,
      },
    },
  ).then((next) => runtimeSessionResponse(next, {
    ...(session.preview_capability_token
      ? {
          preview_capability_token: session.preview_capability_token,
          preview_api_base_url: session.preview_api_base_url,
        }
      : { runtime_api_base_url: session.runtime_api_base_url }),
  }));
};

export const validateTreatmentSessionLocation = (
  session,
  location,
  acceptedCorrectionFingerprint = null,
  locationRole = "patient_service",
) => request(
  session.preview_capability_token
    ? TREATMENT_API_PATH.previewRuntimeSessionLocationValidation(
        session.session_token,
      )
    : TREATMENT_API_PATH.runtimeSessionLocationValidation(
        session.session_token,
      ),
  {
    method: "POST",
    previewCapabilityToken: session.preview_capability_token,
    apiBaseUrl: (
      session.preview_api_base_url || session.runtime_api_base_url
    ),
    body: {
      expected_revision: session.session_revision,
      location,
      location_role: locationRole,
      ...(acceptedCorrectionFingerprint
        ? { accepted_correction_fingerprint: acceptedCorrectionFingerprint }
        : {}),
    },
  },
);

export const uploadTreatmentRuntimeFile = (session, question, file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("expected_revision", String(session.session_revision));
  formData.append("scope", String(question.scope || ""));
  formData.append("source_id", String(question.source_id || question.id || ""));
  formData.append(
    "release_node_id",
    String(question.release_node_id || question.id || ""),
  );
  if (question.program_id) {
    formData.append("program_id", String(question.program_id));
  }
  return request(
    session.preview_capability_token
      ? TREATMENT_API_PATH.previewRuntimeSessionUpload(session.session_token)
      : TREATMENT_API_PATH.runtimeSessionUpload(session.session_token),
    {
      method: "POST",
      previewCapabilityToken: session.preview_capability_token,
      apiBaseUrl: (
        session.preview_api_base_url || session.runtime_api_base_url
      ),
      formData,
      timeoutMs: TREATMENT_API_DEFAULTS.uploadTimeoutMs,
    },
  );
};

export const removeTreatmentRuntimeFile = (session, question, receipt) => request(
  session.preview_capability_token
    ? TREATMENT_API_PATH.previewRuntimeSessionUploadRemoval(session.session_token)
    : TREATMENT_API_PATH.runtimeSessionUploadRemoval(session.session_token),
  {
    method: "DELETE",
    previewCapabilityToken: session.preview_capability_token,
    apiBaseUrl: (
      session.preview_api_base_url || session.runtime_api_base_url
    ),
    body: {
      expected_revision: session.session_revision,
      scope: String(question.scope || ""),
      source_id: String(question.source_id || question.id || ""),
      release_node_id: String(
        question.release_node_id || question.id || "",
      ),
      program_id: String(question.program_id || ""),
      upload_reference: String(receipt?.upload_reference || ""),
    },
  },
);

export const submitTreatmentCheckout = ({
  checkoutCapability,
  runtimeSessionRevision,
  idempotencyKey,
  paymentMethodToken,
  paymentMethod,
  labCheckout,
  selectedProducts,
  apiBaseUrl,
}) => request(TREATMENT_API_PATH.combinedCheckout, {
  method: "POST",
  apiBaseUrl,
  body: {
    checkout_capability: checkoutCapability,
    runtime_session_revision: runtimeSessionRevision,
    idempotency_key: idempotencyKey,
    payment_method_token: paymentMethodToken,
    payment_method: paymentMethod,
    selected_products: selectedProducts
      ? normalizeProductSelections(selectedProducts)
      : undefined,
    lab_checkout: labCheckout || undefined,
  },
});

export const recoverTreatmentCheckout = ({
  checkoutCapability,
  runtimeSessionRevision,
  idempotencyKey,
  recoveryAction,
  treatmentTypeId,
  optionId,
  couponCode,
  apiBaseUrl,
}) => request(TREATMENT_API_PATH.combinedCheckout, {
  method: "POST",
  apiBaseUrl,
  body: {
    operation: TREATMENT_CHECKOUT_OPERATION.recover,
    checkout_capability: checkoutCapability,
    runtime_session_revision: runtimeSessionRevision,
    idempotency_key: idempotencyKey,
    recovery_action: recoveryAction,
    treatment_type_id: treatmentTypeId || undefined,
    option_id: optionId || undefined,
    coupon_code: couponCode === undefined ? undefined : couponCode,
  },
});
